"use client";

import { replyCardStyles, postCardStyles } from "@/lib/styles/post-card-styles";

interface PostHeaderProps {
  pseudonym: string;
  timestamp: string;
  parentPseudonym?: string | null;
  isReply?: boolean;
  isMainPost?: boolean;
  showReplyingTo?: boolean; // Whether to show "Replying to" text (defaults to true for replies)
  replyingToFormat?: "reply-to" | "replying-to-with-at"; // Format for "Replying to" text
  onMouseEnterParent?: () => void;
  onMouseLeaveParent?: () => void;
  onClickParent?: () => void;
}

/**
 * Shared header component for posts and replies
 * Displays pseudonym first (at the top), followed by "Replying to" context below for replies
 * Used by both ReplyCard and MyPostItem for consistent visual hierarchy
 */
export function PostHeader({
  pseudonym,
  timestamp,
  parentPseudonym,
  isReply = false,
  isMainPost = false,
  showReplyingTo,
  replyingToFormat = "reply-to",
  onMouseEnterParent,
  onMouseLeaveParent,
  onClickParent,
}: PostHeaderProps) {
  // Determine if "Replying to" text should be shown
  const shouldShowReplyingTo = showReplyingTo !== undefined 
    ? showReplyingTo 
    : isReply && !!parentPseudonym && parentPseudonym.trim() !== '';

  // Format "Replying to" text based on format prop
  const replyingToText = shouldShowReplyingTo
    ? isMainPost 
      ? "Reply to Original"
      : replyingToFormat === "replying-to-with-at"
      ? `Replying to @${parentPseudonym}`
      : `Reply to ${parentPseudonym}`
    : null;

  // Determine if "Replying to" text should be interactive (for ReplyCard with highlight functionality)
  const isInteractive = !!(onMouseEnterParent || onMouseLeaveParent || onClickParent);

  return (
    <div>
      {/* Pseudonym and timestamp - always first */}
      <div className={isReply ? "flex items-center gap-2" : ""}>
        <p className={isReply ? replyCardStyles.pseudonym : postCardStyles.pseudonym}>
          {pseudonym}
        </p>
        <p className={isReply ? replyCardStyles.timestamp : postCardStyles.timestamp}>
          {timestamp}
        </p>
      </div>
      
      {/* "Replying to" context - show below pseudonym for replies */}
      {shouldShowReplyingTo && replyingToText && (
        <p className="mt-0.5 text-[11px] text-[#a3a3c2]">
          {isInteractive ? (
            <span
              className="cursor-pointer hover:text-[#ff3ea5] transition-colors duration-200 underline-offset-2 hover:underline"
              onMouseEnter={onMouseEnterParent}
              onMouseLeave={onMouseLeaveParent}
              onClick={onClickParent}
            >
              {replyingToText}
            </span>
          ) : (
            <span>{replyingToText}</span>
          )}
        </p>
      )}
    </div>
  );
}
